import React from 'react';
import Link from 'next/link';
import { PulseIconButton, Tooltip } from 'src/components';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faCopy,
  faPencil,
  faTrash,
  faCircleCheck,
  faCircleExclamation,
  faCircleDot,
  faArrowUp,
  faArrowDown,
} from '@fortawesome/pro-solid-svg-icons';
import { STRATEGY_NAMEMAPPING } from 'src/components/Strategy/constants';
import { sentenceCase } from 'src/utils/sentenceCase/sentenceCase';

interface Team {
  id: number;
  name: string;
  description?: string;
}

interface ChainInfo {
  id: number;
  label: string;
}

interface Strategy {
  id?: number;
  label: string;
  description: string;
  type?: string;
  teams?: Team[];
  status?: 'CREATED' | 'READY' | 'ERROR' | 'PROCESSING';
  lastModified?: string;
  created_at?: string;
}

export type SortField =
  | 'status'
  | 'label'
  | 'type'
  | 'lastModified'
  | 'created_at';
export type SortDirection = 'asc' | 'desc';

export interface StrategyTableProps {
  strategies: Strategy[];
  onEdit?: (strategy: Strategy) => void;
  onDelete?: (strategy: Strategy) => void;
  onCopy?: (strategy: Strategy) => void;
  onRowClick?: (strategy: Strategy) => void;
  sortField?: SortField;
  sortDirection?: SortDirection;
  onSort?: (field: SortField, direction: SortDirection) => void;
  className?: string;
  hideTypeColumn?: boolean;
  showTeamsColumn?: boolean;
  chainMap?: Map<number, ChainInfo[]>;
}

const SortIcon: React.FC<{
  field: SortField;
  sortField?: SortField;
  sortDirection?: SortDirection;
}> = ({ field, sortField, sortDirection }) => {
  if (sortField !== field) return null;
  return (
    <Icon
      icon={sortDirection === 'asc' ? faArrowUp : faArrowDown}
      className="w-3 h-3 ml-1"
      aria-hidden="true"
    />
  );
};

const StrategyTable: React.FC<StrategyTableProps> = ({
  strategies,
  onEdit,
  onDelete,
  onCopy,
  onRowClick,
  sortField,
  sortDirection,
  onSort,
  className = '',
  hideTypeColumn = false,
  showTeamsColumn = false,
  chainMap,
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CREATED':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'READY':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'ERROR':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-neutral-100 text-neutral-800 border-neutral-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'CREATED':
        return <Icon icon={faCircleDot} className="w-4 h-4" />;
      case 'PROCESSING':
        return <Icon icon={faCircleDot} className="w-4 h-4 animate-spin" />;
      case 'READY':
        return <Icon icon={faCircleCheck} className="w-4 h-4" />;
      case 'ERROR':
        return <Icon icon={faCircleExclamation} className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'CREATED':
        return sentenceCase(status);
      case 'PROCESSING':
        return 'In Progress';
      case 'READY':
        return 'Editable';
      case 'ERROR':
        return sentenceCase(status);
      default:
        return null;
    }
  };

  const handleSort = (field: SortField) => {
    if (!onSort) return;

    if (sortField === field) {
      // Toggle direction
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      onSort(field, newDirection);
    } else {
      // New field, default to ascending
      onSort(field, 'asc');
    }
  };

  const getAriaSortValue = (
    field: SortField
  ): 'ascending' | 'descending' | 'none' => {
    if (sortField !== field) return 'none';
    return sortDirection === 'asc' ? 'ascending' : 'descending';
  };

  const handleRowKeyDown = (
    e: React.KeyboardEvent<HTMLTableRowElement>,
    strategy: Strategy
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRowClick?.(strategy);
    }
  };

  const buttonIsDisabled = (status?: string) => {
    return !status || status === 'CREATED' || status === 'PROCESSING';
  };

  return (
    <div
      className={`max-h-[calc(100vh-27rem)] overflow-auto rounded-lg border border-neutral-200 ${className}`}
      data-testid="strategy-table"
    >
      <table className="w-full border-collapse min-w-[800px]">
        {/* Table Header */}
        <thead className="sticky top-0 z-10">
          <tr className="bg-neutral-50 border-b border-neutral-200">
            <th
              scope="col"
              className="px-4 py-3 text-left text-xs font-semibold text-neutral-700 cursor-pointer hover:bg-neutral-100 transition-colors"
              onClick={() => handleSort('status')}
              aria-sort={getAriaSortValue('status')}
              data-testid="strategy-table-header-status"
            >
              <div className="flex items-center">
                Status
                <SortIcon
                  field="status"
                  sortField={sortField}
                  sortDirection={sortDirection}
                />
              </div>
            </th>
            <th
              scope="col"
              className="px-4 py-3 text-left text-xs font-semibold text-neutral-700 cursor-pointer hover:bg-neutral-100 transition-colors"
              onClick={() => handleSort('label')}
              aria-sort={getAriaSortValue('label')}
              data-testid="strategy-table-header-name"
            >
              <div className="flex items-center">
                Name
                <SortIcon
                  field="label"
                  sortField={sortField}
                  sortDirection={sortDirection}
                />
              </div>
            </th>
            {chainMap && (
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-semibold text-neutral-700"
                data-testid="strategy-table-header-chain"
              >
                Chain
              </th>
            )}
            {!hideTypeColumn && (
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-semibold text-neutral-700 cursor-pointer hover:bg-neutral-100 transition-colors"
                onClick={() => handleSort('type')}
                aria-sort={getAriaSortValue('type')}
                data-testid="strategy-table-header-type"
              >
                <div className="flex items-center">
                  Type
                  <SortIcon
                    field="type"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </div>
              </th>
            )}
            {showTeamsColumn && (
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-semibold text-neutral-700"
                data-testid="strategy-table-header-teams"
              >
                Teams
              </th>
            )}
            <th
              scope="col"
              className="px-4 py-3 text-left text-xs font-semibold text-neutral-700 cursor-pointer hover:bg-neutral-100 transition-colors"
              onClick={() => handleSort('created_at')}
              aria-sort={getAriaSortValue('created_at')}
              data-testid="strategy-table-header-created-at"
            >
              <div className="flex items-center">
                Created At
                <SortIcon
                  field="created_at"
                  sortField={sortField}
                  sortDirection={sortDirection}
                />
              </div>
            </th>
            <th
              scope="col"
              className="px-4 py-3 text-right text-xs font-semibold text-neutral-700"
              data-testid="strategy-table-header-actions"
            >
              Actions
            </th>
          </tr>
        </thead>

        {/* Table Body */}
        <tbody data-testid="strategy-table-body">
          {strategies.map((strategy) => (
            <tr
              key={strategy.id}
              className="border-b border-neutral-200 last:border-b-0 hover:bg-neutral-50 transition-colors cursor-pointer"
              onClick={() => onRowClick?.(strategy)}
              onKeyDown={(e) => handleRowKeyDown(e, strategy)}
              tabIndex={onRowClick ? 0 : undefined}
              role={onRowClick ? 'button' : undefined}
              aria-label={
                onRowClick ? `View strategy: ${strategy.label}` : undefined
              }
              data-testid={`strategy-row-${strategy.id}`}
            >
              {/* Status Column */}
              <td className="px-4 py-3 w-32">
                {strategy.status && (
                  <div
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border w-fit ${getStatusColor(
                      strategy.status
                    )}`}
                    data-testid={`strategy-status-${strategy.id}`}
                  >
                    {getStatusIcon(strategy.status)}
                    <span className="whitespace-nowrap">
                      {getStatusText(strategy.status)}
                    </span>
                  </div>
                )}
              </td>

              {/* Name Column */}
              <td className="px-4 py-3">
                <div className="flex flex-col gap-1">
                  <h3
                    className="text-sm font-semibold text-primary"
                    data-testid={`strategy-name-${strategy.id}`}
                  >
                    {strategy.label}
                  </h3>
                  <Tooltip content={strategy.description}>
                    <p
                      className="text-xs text-secondary truncate max-w-md"
                      data-testid={`strategy-description-${strategy.id}`}
                    >
                      {strategy.description}
                    </p>
                  </Tooltip>
                </div>
              </td>

              {/* Chain Column */}
              {chainMap && (
                <td className="px-4 py-3 min-w-64">
                  {strategy.id && chainMap.get(strategy.id) && (
                    <div
                      className="flex flex-wrap gap-1"
                      data-testid={`strategy-chains-${strategy.id}`}
                    >
                      {chainMap.get(strategy.id)!.map((chain) => (
                        <Link
                          key={chain.id}
                          href={`/settings/admin/strategy-manager/chain/${chain.id}/edit`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-violet-100 text-violet-800 hover:bg-violet-200 transition-colors"
                          data-testid={`strategy-chain-${strategy.id}-${chain.id}`}
                        >
                          {chain.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </td>
              )}

              {/* Type Column */}
              {!hideTypeColumn && (
                <td className="px-4 py-3 w-40">
                  {strategy.type && (
                    <span
                      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap"
                      data-testid={`strategy-type-${strategy.id}`}
                    >
                      {STRATEGY_NAMEMAPPING[strategy.type]}
                    </span>
                  )}
                </td>
              )}

              {/* Teams Column */}
              {showTeamsColumn && (
                <td className="px-4 py-3">
                  {strategy.teams && strategy.teams.length > 0 && (
                    <div
                      className="flex flex-wrap gap-1"
                      data-testid={`strategy-teams-${strategy.id}`}
                    >
                      {strategy.teams.map((team) => (
                        <span
                          key={team.id}
                          className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-sky-100 text-sky-800"
                          data-testid={`strategy-team-${strategy.id}-${team.id}`}
                        >
                          {team.name}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
              )}
              {/* Created At Column */}
              <td className="px-4 py-3 w-40">
                <span
                  className="flex items-center px-2 py-0.5 text-sm font-medium text-secondary"
                  data-testid={`strategy-created-at-${strategy.id}`}
                >
                  {strategy.created_at
                    ? new Date(strategy.created_at).toLocaleDateString()
                    : 'N/A'}
                </span>
              </td>

              {/* Actions Column */}
              <td className="px-4 py-3 w-40">
                <div
                  className="flex items-center justify-end gap-2"
                  data-testid={`strategy-actions-${strategy.id}`}
                >
                  {onEdit && (
                    <PulseIconButton
                      icon={faPencil}
                      variant="soft"
                      color="primary"
                      size="sm"
                      aria-label={`Edit strategy: ${strategy.label}`}
                      tooltip="Edit Strategy"
                      disabled={buttonIsDisabled(strategy.status)}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(strategy);
                      }}
                    />
                  )}

                  {onCopy && (
                    <PulseIconButton
                      icon={faCopy}
                      variant="soft"
                      color="neutral"
                      size="sm"
                      disabled={buttonIsDisabled(strategy.status)}
                      aria-label={
                        buttonIsDisabled(strategy.status)
                          ? `Cannot copy strategy: ${strategy.label} - must be ready`
                          : `Copy strategy: ${strategy.label}`
                      }
                      tooltip={
                        buttonIsDisabled(strategy.status)
                          ? 'Strategy must be ready to copy'
                          : 'Copy Strategy'
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!buttonIsDisabled(strategy.status)) {
                          onCopy(strategy);
                        }
                      }}
                    />
                  )}

                  {onDelete && (
                    <PulseIconButton
                      icon={faTrash}
                      variant="soft"
                      color="danger"
                      size="sm"
                      aria-label={`Delete strategy: ${strategy.label}`}
                      tooltip="Delete Strategy"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(strategy);
                      }}
                    />
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Empty State */}
      {strategies.length === 0 && (
        <div
          className="text-center py-12 text-neutral-500"
          data-testid="strategy-table-empty"
          role="status"
          aria-live="polite"
        >
          <p className="text-sm">No strategies found</p>
        </div>
      )}
    </div>
  );
};

export default StrategyTable;
